/*
 * KANBAN EDITOR COMPONENT
 * 
 * Configuration panel for Kanban board settings, displayed in the right panel Editor tab.
 * Uses controlled component pattern - all state managed by parent (Kanban.tsx).
 * 
 * Features:
 * - Edit board name (with local state for performance - syncs on blur/save)
 * - Compact data source toggle aligned with section label
 * - Type selector dropdown
 * - Group field selector (select-type fields only)
 * - Lane visibility checkboxes with inline, wrapping layout
 * - Record filters with dynamic operators
 * - Action buttons: Reset, Cancel, Save
 * - Live preview: all changes immediately reflect in the board
 * 
 * Performance:
 * - Name input uses local state to avoid context round-trips on every keystroke
 * - Syncs to parent state on blur or before save
 * 
 * Props:
 * - config: Current KanbanConfig
 * - onConfigChange: Callback to update config (partial updates)
 * - onSave, onReset, onCancel: Action handlers
 * - typeGroups, schemaFields, allSchemaFields: Schema data for dropdowns
 * - hasChanges: Whether there are unsaved changes
 */

import { FC, useMemo, useState, useEffect, useRef } from 'react';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { type Schema } from '../../context/SchemaContext';
import { type FormField } from '../common/FormField';
import {
  type KanbanConfig,
  type FilterGroup,
  type FilterRule,
  type FilterOperator,
  createFilterRule,
  getOperatorsForFieldType,
  OPERATOR_LABELS
} from '../../models/kanban';

// Use FilterOperator in type assertion
const _unusedFilterOperator: FilterOperator = 'is';
void _unusedFilterOperator;
import { buildFilterSummary } from '../../utils/kanbanFilters';
import useDebounce from '../../hooks/useDebounce';

interface TypeOption {
  id: string;
  name: string;
  entityType: number;
  schema?: Schema;
}

interface TypeGroup {
  label: string;
  options: TypeOption[];
}

export interface KanbanEditorProps {
  config: KanbanConfig;
  savedConfig?: KanbanConfig | null;
  accountSchemas?: Schema[];
  contactSchemas?: Schema[];
  typeGroups?: TypeGroup[];
  schemaFields?: FormField[];
  allSchemaFields?: FormField[];
  selectedType?: TypeOption | null;
  hasChanges?: boolean;
  onConfigChange?: (partial: Partial<KanbanConfig>) => void;
  onSave?: () => void;
  onReset?: () => void;
  onCancel?: () => void;
  onClose?: () => void; // Close the right panel
}

const KanbanEditor: FC<KanbanEditorProps> = ({
  config,
  savedConfig: _savedConfig,
  accountSchemas: _accountSchemas = [],
  contactSchemas: _contactSchemas = [],
  typeGroups = [],
  schemaFields = [],
  allSchemaFields = [],
  selectedType: _selectedType,
  hasChanges = false,
  onConfigChange,
  onSave,
  onReset,
  onCancel,
  onClose
}) => {
  // Unused props preserved for future use
  void _savedConfig; void _accountSchemas; void _contactSchemas; void _selectedType;
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isFieldDropdownOpen, setIsFieldDropdownOpen] = useState(false);

  // Local state for name input to avoid slow context round-trips on every keystroke
  const [localName, setLocalName] = useState(config.name);
  const debouncedName = useDebounce(localName, 150);
  const lastSyncedName = useRef(config.name);

  // Sync local name when config.name changes from outside (e.g., loading a saved board)
  // Only sync if it's NOT a value we sent ourselves
  useEffect(() => {
    if (config.name !== lastSyncedName.current) {
      // External change - sync to local
      setLocalName(config.name);
      lastSyncedName.current = config.name;
    }
  }, [config.name]);

  // Debounced sync to parent - updates board title while typing
  useEffect(() => {
    if (debouncedName !== config.name) {
      lastSyncedName.current = debouncedName;
      onConfigChange?.({ name: debouncedName });
    }
  }, [debouncedName]);

  // Sync name to parent on blur (immediate)
  const handleNameBlur = () => {
    if (localName !== config.name) {
      lastSyncedName.current = localName;
      onConfigChange?.({ name: localName });
    }
  };

  // Handle save - sync local name first
  const handleSave = () => {
    if (localName !== config.name) {
      lastSyncedName.current = localName;
      onConfigChange?.({ name: localName });
    }
    // Use setTimeout to ensure state update is processed before save
    setTimeout(() => onSave?.(), 0);
  };

  // Get field options for lane checkboxes
  const selectedField = useMemo(() => {
    return schemaFields.find(f => f.name === config.groupField);
  }, [schemaFields, config.groupField]);

  const fieldOptions = useMemo(() => {
    if (!selectedField?.options) return [];
    return selectedField.options.map(opt => {
      if (typeof opt === 'string') return { label: opt, value: opt };
      return { label: String(opt.label), value: String(opt.value) };
    });
  }, [selectedField]);

  // Build field labels map for filter summary
  const fieldLabelsMap = useMemo(() => {
    const map: Record<string, string> = {};
    allSchemaFields.forEach(f => {
      map[f.name] = f.label || f.name;
    });
    return map;
  }, [allSchemaFields]);

  const filterSummary = useMemo(() => {
    return buildFilterSummary(config.filters, fieldLabelsMap);
  }, [config.filters, fieldLabelsMap]);

  const handleClose = () => {
    // Reset form state first
    onCancel?.();
    // Close the panel
    onClose?.();
  };

  const handleDataSourceChange = (entityType: 'accounts' | 'contacts') => {
    if (entityType === config.entityType) return;

    // Clear type, field, and filters when changing data source
    onConfigChange?.({
      entityType,
      typeId: '',
      typeName: '',
      groupField: '',
      groupFieldLabel: '',
      visibleLanes: [],
      filters: { operator: 'AND', rules: [], groups: [] }
    });
  };

  const handleTypeSelect = (option: TypeOption) => {
    onConfigChange?.({
      typeId: option.id,
      typeName: option.name,
      entityType: option.entityType === 2 ? 'accounts' : 'contacts',
      groupField: '',
      groupFieldLabel: '',
      visibleLanes: [],
      filters: { operator: 'AND', rules: [], groups: [] }  // Clear filters when type changes
    });
    setIsTypeDropdownOpen(false);
  };

  const handleFieldSelect = (field: FormField) => {
    onConfigChange?.({
      groupField: field.name,
      groupFieldLabel: field.label || field.name,
      visibleLanes: [] // Reset visible lanes when field changes
    });
    setIsFieldDropdownOpen(false);
  };

  const handleLaneToggle = (laneValue: string) => {
    const currentVisible = config.visibleLanes || [];
    let newVisible: string[];

    if (currentVisible.length === 0) {
      // Currently showing all - hide this one by including all others
      newVisible = fieldOptions
        .map(opt => opt.value)
        .filter(v => v !== laneValue);
    } else if (currentVisible.includes(laneValue)) {
      // Remove from visible
      newVisible = currentVisible.filter(v => v !== laneValue);
    } else {
      // Add to visible
      newVisible = [...currentVisible, laneValue];
    }

    onConfigChange?.({ visibleLanes: newVisible });
  };

  const isLaneVisible = (laneValue: string): boolean => {
    if (!config.visibleLanes || config.visibleLanes.length === 0) return true;
    return config.visibleLanes.includes(laneValue);
  };

  // Filter rule handlers
  const addFilterRule = () => {
    const newRule = createFilterRule();
    const newFilters: FilterGroup = {
      ...config.filters,
      rules: [...config.filters.rules, newRule]
    };
    onConfigChange?.({ filters: newFilters });
  };

  const updateFilterRule = (ruleId: string, updates: Partial<FilterRule>) => {
    const newRules = config.filters.rules.map(rule =>
      rule.id === ruleId ? { ...rule, ...updates } : rule
    );
    onConfigChange?.({ filters: { ...config.filters, rules: newRules } });
  };

  const removeFilterRule = (ruleId: string) => {
    const newRules = config.filters.rules.filter(rule => rule.id !== ruleId);
    onConfigChange?.({ filters: { ...config.filters, rules: newRules } });
  };

  // Get available types based on selected entity type
  const availableTypes = useMemo(() => {
    return typeGroups.find(g =>
      (config.entityType === 'accounts' && g.label === 'Accounts') ||
      (config.entityType === 'contacts' && g.label === 'Contacts')
    )?.options || [];
  }, [typeGroups, config.entityType]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 border-b ${THEME_CLASSES.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <input
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={handleNameBlur}
              placeholder="Enter board name..."
              className={`text-lg font-semibold ${THEME_CLASSES.text.primary} bg-transparent border-none outline-none w-full placeholder:${THEME_CLASSES.text.secondary}`}
            />
          </div>
          <button
            type="button"
            onClick={handleClose}
            className={`p-1.5 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
            title="Close editor"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* 1. Data Source */}
        <section>
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className={`text-sm font-medium ${THEME_CLASSES.text.primary}`}>
              1. Data Source
            </h3>
            <div className="flex rounded-md border overflow-hidden">
              <button
                type="button"
                onClick={() => handleDataSourceChange('accounts')}
                className={`px-2.5 py-1.5 text-xs font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${config.entityType === 'accounts'
                  ? 'bg-blue-600 text-white'
                  : `${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`
                  }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Accounts
              </button>
              <button
                type="button"
                onClick={() => handleDataSourceChange('contacts')}
                className={`px-2.5 py-1.5 text-xs font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${config.entityType === 'contacts'
                  ? 'bg-blue-600 text-white'
                  : `${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`
                  }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Contacts
              </button>
            </div>
          </div>

          {/* Type Selector */}
          {availableTypes.length > 0 && (
            <div className="mt-3 relative">
              <button
                onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text}`}
              >
                <span>{config.typeName || 'Select type...'}</span>
                <svg className={`w-4 h-4 transition-transform ${isTypeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isTypeDropdownOpen && (
                <div className={`absolute z-10 mt-1 w-full rounded-md shadow-lg bg-white dark:bg-gray-800 border ${THEME_CLASSES.border} max-h-48 overflow-auto`}>
                  {availableTypes.map((option) => (
                    <div
                      key={option.id}
                      onClick={() => handleTypeSelect(option)}
                      className={`px-3 py-2 text-sm cursor-pointer ${config.typeId === option.id
                        ? 'bg-blue-600 text-white'
                        : `${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background}`
                        }`}
                    >
                      {option.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* 2. Group Cards By */}
        <section>
          <h3 className={`text-sm font-medium ${THEME_CLASSES.text.primary} mb-3`}>
            2. Group Cards By
          </h3>

          {/* Field Selector */}
          <div className="relative mb-3">
            <button
              onClick={() => setIsFieldDropdownOpen(!isFieldDropdownOpen)}
              disabled={schemaFields.length === 0}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${schemaFields.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span>{config.groupFieldLabel || 'Select field...'}</span>
              <svg className={`w-4 h-4 transition-transform ${isFieldDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isFieldDropdownOpen && schemaFields.length > 0 && (
              <div className={`absolute z-10 mt-1 w-full rounded-md shadow-lg bg-white dark:bg-gray-800 border ${THEME_CLASSES.border} max-h-48 overflow-auto`}>
                {schemaFields.map((field) => (
                  <div
                    key={field.name}
                    onClick={() => handleFieldSelect(field)}
                    className={`px-3 py-2 text-sm cursor-pointer ${config.groupField === field.name
                      ? 'bg-blue-600 text-white'
                      : `${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background}`
                      }`}
                  >
                    {field.label || field.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Lane Checkboxes */}
          {fieldOptions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {fieldOptions.map((option) => (
                <label
                  key={option.value}
                  className={`inline-flex items-center gap-2 cursor-pointer rounded-md border px-2.5 py-1.5 text-sm ${THEME_CLASSES.text.primary} ${THEME_CLASSES.border}`}
                >
                  <input
                    type="checkbox"
                    checked={isLaneVisible(option.value)}
                    onChange={() => handleLaneToggle(option.value)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
              <label className={`inline-flex items-center gap-2 cursor-pointer rounded-md border px-2.5 py-1.5 text-sm ${THEME_CLASSES.text.primary} ${THEME_CLASSES.border}`}>
                <input
                  type="checkbox"
                  checked={config.includeUnassigned}
                  onChange={() => onConfigChange?.({ includeUnassigned: !config.includeUnassigned })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Include "Unassigned" lane</span>
              </label>
            </div>
          )}
        </section>

        {/* 3. Filters */}
        <section>
          <h3 className={`text-sm font-medium ${THEME_CLASSES.text.primary} mb-3`}>
            3. Filters
          </h3>

          {/* Filter Summary */}
          {filterSummary && (
            <p className={`text-xs ${THEME_CLASSES.text.secondary} mb-3 italic`}>
              {filterSummary}
            </p>
          )}

          {/* Filter Rules */}
          <div className="space-y-2">
            {config.filters.rules.map((rule, index) => (
              <FilterRuleRow
                key={rule.id}
                rule={rule}
                fields={allSchemaFields}
                showOperator={index > 0}
                groupOperator={config.filters.operator}
                onUpdate={(updates) => updateFilterRule(rule.id, updates)}
                onRemove={() => removeFilterRule(rule.id)}
              />
            ))}
          </div>

          {/* Add Rule Button */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={addFilterRule}
              className={`text-sm ${THEME_CLASSES.text.secondary} hover:text-blue-600 flex items-center gap-1`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add rule
            </button>
          </div>
        </section>
      </div>

      {/* Footer Actions */}
      <div className={`px-4 py-3 border-t ${THEME_CLASSES.border} flex items-center justify-between`}>
        <button
          type="button"
          onClick={onReset}
          className={`px-3 py-1.5 text-sm rounded-md border ${THEME_CLASSES.border} ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={!localName.trim() || (!hasChanges && localName === config.name)}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${localName.trim() && (hasChanges || localName !== config.name)
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
        >
          Save Board
        </button>
      </div>
    </div>
  );
};

// Filter Rule Row Component
interface FilterRuleRowProps {
  rule: FilterRule;
  fields: FormField[];
  showOperator: boolean;
  groupOperator: 'AND' | 'OR';
  onUpdate: (updates: Partial<FilterRule>) => void;
  onRemove: () => void;
}

const FilterRuleRow: FC<FilterRuleRowProps> = ({
  rule,
  fields,
  showOperator,
  groupOperator,
  onUpdate,
  onRemove
}) => {
  const [isFieldOpen, setIsFieldOpen] = useState(false);
  const [isOperatorOpen, setIsOperatorOpen] = useState(false);
  const [isValueOpen, setIsValueOpen] = useState(false);

  const selectedField = fields.find(f => f.name === rule.field);
  const operators: FilterOperator[] = selectedField ? getOperatorsForFieldType(selectedField.type) : ['is'];

  const valueOptions = selectedField?.options?.map(opt => {
    if (typeof opt === 'string') return { label: opt, value: opt };
    return { label: String(opt.label), value: String(opt.value) };
  }) || [];

  // Add "Me" option for owner-like fields
  const isOwnerField = rule.field.toLowerCase().includes('owner') || rule.field.toLowerCase().includes('assigned');
  if (isOwnerField) {
    valueOptions.unshift({ label: 'Me', value: '__ME__' });
  }

  return (
    <div className="flex items-center gap-2">
      {showOperator && (
        <span className={`text-xs ${THEME_CLASSES.text.secondary} w-10`}>
          {groupOperator}
        </span>
      )}

      {/* Field Selector */}
      <div className="relative flex-1">
        <button
          onClick={() => setIsFieldOpen(!isFieldOpen)}
          className={`w-full px-2 py-1.5 text-xs border rounded ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} text-left`}
        >
          {selectedField?.label || rule.field || 'Field...'}
        </button>
        {isFieldOpen && (
          <div className={`absolute z-20 mt-1 w-full rounded shadow-lg bg-white dark:bg-gray-800 border ${THEME_CLASSES.border} max-h-40 overflow-auto`}>
            {fields.map((field) => (
              <div
                key={field.name}
                onClick={() => {
                  onUpdate({ field: field.name, operator: 'is', value: '' });
                  setIsFieldOpen(false);
                }}
                className={`px-2 py-1.5 text-xs cursor-pointer ${THEME_CLASSES.hover.background}`}
              >
                {field.label || field.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Operator Selector */}
      <div className="relative w-24">
        <button
          onClick={() => setIsOperatorOpen(!isOperatorOpen)}
          className={`w-full px-2 py-1.5 text-xs border rounded ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} text-left`}
        >
          {OPERATOR_LABELS[rule.operator] || rule.operator}
        </button>
        {isOperatorOpen && (
          <div className={`absolute z-20 mt-1 w-full rounded shadow-lg bg-white dark:bg-gray-800 border ${THEME_CLASSES.border} max-h-40 overflow-auto`}>
            {operators.map((op) => (
              <div
                key={op}
                onClick={() => {
                  onUpdate({ operator: op });
                  setIsOperatorOpen(false);
                }}
                className={`px-2 py-1.5 text-xs cursor-pointer ${THEME_CLASSES.hover.background}`}
              >
                {OPERATOR_LABELS[op]}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Value Input */}
      {valueOptions.length > 0 ? (
        <div className="relative flex-1">
          <button
            onClick={() => setIsValueOpen(!isValueOpen)}
            className={`w-full px-2 py-1.5 text-xs border rounded ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} text-left`}
          >
            {rule.value === '__ME__' ? 'Me' : (rule.value || 'Value...')}
          </button>
          {isValueOpen && (
            <div className={`absolute z-20 mt-1 w-full rounded shadow-lg bg-white dark:bg-gray-800 border ${THEME_CLASSES.border} max-h-40 overflow-auto`}>
              {valueOptions.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => {
                    onUpdate({ value: opt.value });
                    setIsValueOpen(false);
                  }}
                  className={`px-2 py-1.5 text-xs cursor-pointer ${THEME_CLASSES.hover.background}`}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <input
          type="text"
          value={typeof rule.value === 'string' ? rule.value : ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Value..."
          className={`flex-1 px-2 py-1.5 text-xs border rounded ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text}`}
        />
      )}

      {/* Remove Button */}
      <button
        onClick={onRemove}
        className={`p-1 rounded ${THEME_CLASSES.text.secondary} hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default KanbanEditor;
